import React, { useEffect } from 'react';
import Hero from '../components/Hero';
import CourseContent from '../components/CourseContent';
import PricingSection from '../components/PricingSection';
import TestimonialFeature from '../components/TestimonialFeature';
import SecretFormula from '../components/SecretFormula';
import SucceedSection from '../components/SucceedSection';
import WhoItsFor from '../components/WhoItsFor';
import FAQ from '../components/FAQ';
import ComparisonTable from '../components/ComparisonTable';
import CareerPath from '../components/CareerPath';
import Navbar from '../components/Navbar';
import StudentTestimonials from '../components/StudentTestimonials';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const CAPPage: React.FC = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if this is an email confirmation
    const urlParams = new URLSearchParams(window.location.search);
    const isEmailConfirmed = urlParams.get('confirmed') === 'true';
    
    // If this is an email confirmation, update the user's status
    if (isEmailConfirmed) {
      const userData = localStorage.getItem('kloudskool_user');
      if (userData) {
        const user = JSON.parse(userData);
        user.emailConfirmed = true;
        localStorage.setItem('kloudskool_user', JSON.stringify(user));
      }
      return; // Allow access to the page
    }

    // For direct access, check if user exists and is confirmed
    const userData = localStorage.getItem('kloudskool_user');
    if (!userData) {
      // Redirect back to home if they didn't register
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to scroll to pricing section
  const scrollToPricing = () => {
    const pricingSection = document.querySelector('#pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Cloud engineering curriculum items
  const curriculum = {
    introSection: {
      title: "Cloud Engineering Bootcamp",
      subtitle: "Become a Certified Azure Cloud Engineer in 6 Months",
      mainPoints: [
        "Hands-on Azure Cloud training",
        "Infrastructure as Code with Terraform",
        "CI/CD with GitHub Actions",
        "Real-world projects and portfolio building"
      ]
    },
    topics: [
      {
        title: "Setting Up Your Development Environment",
        description: "Learn how to set up VS Code with essential extensions, configure your terminal, and create an optimal development environment for cloud engineering.",
        emoji: "💻"
      },
      {
        title: "Linux Command Line Mastery",
        description: "Master the essential Linux commands and concepts that every cloud engineer needs to know for effective infrastructure management.",
        emoji: "🐧"
      },
      {
        title: "Git & GitHub Workflow",
        description: "Learn version control best practices and how to collaborate effectively using Git and GitHub in a professional cloud engineering context.",
        emoji: "🔄"
      },
      {
        title: "Azure Cloud Foundations",
        description: "Get hands-on experience with Azure Portal and learn how to deploy and manage cloud resources manually before automation.",
        emoji: "☁️"
      },
      {
        title: "Infrastructure as Code with Terraform",
        description: "Master infrastructure automation using Terraform, from basic concepts to advanced deployment strategies.",
        emoji: "🏗️"
      },
      {
        title: "CI/CD Pipeline Mastery",
        description: "Learn how to build and maintain robust CI/CD pipelines using GitHub Actions for automated infrastructure deployment.",
        emoji: "🚀"
      },
      {
        title: "Capstone AI Project",
        description: "Apply everything you've learned to build and deploy a production-ready AI application on Azure. Implement security best practices, monitoring, and cost optimization while showcasing your cloud engineering expertise.",
        emoji: "🎯"
      },
      {
        title: "Land Your Dream Role",
        description: "Get guidance on resume building, interview preparation, and job search strategies. Learn how to showcase your projects and negotiate the best offers.",
        emoji: "💼"
      }
    ],
    pricingOptions: {
      title: "Book a Free Qualification Meeting",
      options: [
        {
          title: "30-Minute Free Call",
          highlighted: true,
          features: [
            "Assess your goals and career aspirations",
            "Review qualification criteria",
            "Explain the purpose of the Cloud Acceleration Program",
            "Answer all your questions about the program"
          ],
          bookingLink: "https://cal.com/kloudskool/30min",
          ctaText: "Book Your Free Call"
        }
      ]
    },
    testimonials: [
      {
        name: "Fouad",
        role: "Aspiring Cloud Engineer",
        company: "",
        image: "/testimonials/fouad.png",
        quote: "I'm really enjoying this accelerated program! Even though I haven't finished it yet, I appreciate how it's focused on real-world applications. The concepts are taught from a work-based perspective, showing how they're actually used in the industry. It's been a great learning experience so far."
      },
      {
        name: "Amal",
        role: "Aspiring Cloud Engineer",
        company: "",
        image: "https://ui-avatars.com/api/?name=Amal&background=0EA5E9&color=fff",
        quote: "A huge thank you to Faisal for this opportunity! When I started CAP on December 11th, I had zero IT background, and everything felt overwhelming, Linux made no sense, and short lessons felt like huge challenges. But I kept pushing through. Now, nearing the end of GitHub Fundamentals, things are finally clicking."
      },
      {
        name: "Ashraf",
        role: "Aspiring Cloud Engineer",
        company: "",
        image: "/testimonials/ashraf.svg",
        quote: "I found it a bit challenging when I first started the course, as I had no prior knowledge of IT. However, as I followed the videos and began practicing, everything started to make sense. The course has helped me understand IT concepts much better, and it's very well-structured and easy to follow. I would rate it 10/10!"
      },
      {
        name: "Abokor",
        role: "Senior Consultant",
        company: "VMware",
        image: "/testimonials/abokor.svg",
        quote: "My experience with Kloud Skool has been incredibly positive. I've learned so much through their practical, straight-to-the-point courses. They focus on exactly what you need to succeed in cloud computing. The hands-on approach makes it easy to build real-world skills, and I now feel much more confident in my cloud expertise. I highly recommend Kloud Skool to anyone looking to grow in this field."
      }
    ]
  };

  return (
    <div>
      <Navbar onEnrollClick={scrollToPricing} />
      <Hero />
      <TestimonialFeature />
      <SecretFormula />
      <CourseContent 
        modules={curriculum.topics}
      />
      <CareerPath />
      <WhoItsFor />
      <SucceedSection />
      <ComparisonTable />
      <div className="bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0F9FF]">
        <div id="pricing-section">
          <PricingSection 
            title={curriculum.pricingOptions.title} 
            options={curriculum.pricingOptions.options}
          />
        </div>
      </div>
      <StudentTestimonials testimonials={curriculum.testimonials} />
      <FAQ />
      <div className="bg-[#15213A] text-white py-16 text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Cloud Engineering Journey?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8 text-lg">
            Talk to our team about your career goals and find out if the Cloud Acceleration Program is the right fit for you.
          </p>
          <button 
            onClick={scrollToPricing}
            className="bg-[#F97316] hover:bg-[#F97316]/90 text-white font-bold py-4 px-10 rounded-lg text-xl transition duration-300 shadow-lg"
          >
            Book Your Free 30-Minute Consultation
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CAPPage;